import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import {
  Plus,
  Edit2,
  Trash2,
  Download,
  RefreshCw,
  X,
  Save,
  AlertCircle,
  Check,
  Calendar as CalendarIcon,
  FileText,
  Paperclip,
  MessageCircleQuestion,
  ChevronDown
} from 'lucide-react';
import type { User } from '../../types';
import { todayLocal } from '../../utils/localDate';

interface MeetingDocument {
  filename: string;
  originalName: string;
  path: string;
  size: number;
  mimetype: string;
  uploadedAt: string;
  uploadedBy: string;
}

interface TopicAssignee {
  id: string;
  name: string;
  email: string;
}

interface Topic {
  text: string;
  assignedTo: TopicAssignee[];
}

interface TLMeeting {
  _id: string;
  date: string;
  topics: Topic[];
  summary: string;
  studyDocuments: MeetingDocument[];
  discussionDocuments: MeetingDocument[];
  createdBy?: { name?: string; email?: string };
  lastUpdatedBy?: { name?: string; email?: string };
  createdAt?: string;
}

interface TLUser {
  id: string;
  name: string;
  email: string;
}

interface TLMeetingsProps {
  currentUser: User;
}

const MAX_TOPICS = 3;
const MAX_ASSIGNEES_PER_TOPIC = 2;

const emptyTopic = (): Topic => ({ text: '', assignedTo: [] });

const emptyForm = () => ({
  date: todayLocal(),
  summary: '',
  topics: [emptyTopic()]
});

const formatBytes = (bytes: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const weekdayOf = (dateStr: string) => {
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
  } catch {
    return '';
  }
};

const formatDateHeading = (dateStr: string) => {
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Shared by the "Log New Meeting" form and the inline edit form — a dynamic
// 1-3 row topic list, each with a text field and a react-select assignee
// picker capped at 2 people. Whoever isn't assigned to a topic is expected to
// show up with questions for it instead of presenting.
const TopicsEditor: React.FC<{
  topics: Topic[];
  onChange: (topics: Topic[]) => void;
  tlUsers: TLUser[];
}> = ({ topics, onChange, tlUsers }) => {
  const updateTopic = (index: number, patch: Partial<Topic>) => {
    onChange(topics.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  };

  const addTopic = () => {
    if (topics.length >= MAX_TOPICS) return;
    onChange([...topics, emptyTopic()]);
  };

  const removeTopic = (index: number) => {
    onChange(topics.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Topics (up to {MAX_TOPICS}) *
        </label>
        {topics.length < MAX_TOPICS && (
          <button
            type="button"
            onClick={addTopic}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            + Add another topic
          </button>
        )}
      </div>

      {topics.map((topic, idx) => (
        <div key={idx} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-2 bg-gray-50 dark:bg-gray-700/40">
          <div className="flex items-start gap-2">
            <input
              type="text"
              value={topic.text}
              onChange={(e) => updateTopic(idx, { text: e.target.value })}
              placeholder={`Topic ${idx + 1} — e.g., Keycloak role mapping deep-dive`}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            {topics.length > 1 && (
              <button
                type="button"
                onClick={() => removeTopic(idx)}
                className="p-2 text-gray-400 hover:text-red-600"
                title="Remove this topic"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div>
            <Select
              isMulti
              options={tlUsers.map(u => ({ value: u.id, label: u.name }))}
              value={topic.assignedTo.map(a => ({ value: a.id, label: a.name }))}
              onChange={(selected: any) => {
                const picked = (selected || []).slice(0, MAX_ASSIGNEES_PER_TOPIC);
                updateTopic(idx, {
                  assignedTo: picked.map((opt: any) => {
                    const u = tlUsers.find(u => u.id === opt.value);
                    return { id: opt.value, name: opt.label, email: u?.email || '' };
                  })
                });
              }}
              placeholder="Who's presenting this topic? (max 2)"
              classNamePrefix="react-select"
              styles={{ control: (base) => ({ ...base, minHeight: '36px', fontSize: '0.875rem' }) }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Everyone else is expected to come with questions for this topic.
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

const TLMeetings: React.FC<TLMeetingsProps> = ({ currentUser }) => {
  const [meetings, setMeetings] = useState<TLMeeting[]>([]);
  const [tlUsers, setTlUsers] = useState<TLUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showNewForm, setShowNewForm] = useState(false);
  const [newMeeting, setNewMeeting] = useState(emptyForm());
  const [newStudyFiles, setNewStudyFiles] = useState<File[]>([]);
  const [newDiscussionFiles, setNewDiscussionFiles] = useState<File[]>([]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<{ topics: Topic[]; summary: string; date: string } | null>(null);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [editStudyFiles, setEditStudyFiles] = useState<File[]>([]);
  const [editDiscussionFiles, setEditDiscussionFiles] = useState<File[]>([]);

  const canEdit = ['tl', 'admin'].includes(currentUser.role);

  useEffect(() => {
    fetchMeetings();
    fetchTLUsers();
  }, []);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch('/api/tl-meetings');
      if (!response.ok) throw new Error('Failed to fetch meetings');
      const data = await response.json();
      setMeetings(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch meetings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTLUsers = async () => {
    try {
      const response = await fetch('/api/users');
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      // /api/users' own `role` field is just roles[0], which can land on a
      // default Keycloak realm role instead of the real one — check the
      // full roles array first.
      const tls = (Array.isArray(data) ? data : []).filter((u: any) => {
        const roles = u.realmRoles || u.roles || [];
        return roles.some((r: string) => r.toLowerCase() === 'tl') || u.role === 'tl';
      });
      setTlUsers(tls.map((u: any) => ({ id: u.id, name: u.name || u.username, email: u.email })));
    } catch (err) {
      console.error('Failed to fetch TL users:', err);
    }
  };

  const resetNewForm = () => {
    setShowNewForm(false);
    setNewMeeting(emptyForm());
    setNewStudyFiles([]);
    setNewDiscussionFiles([]);
  };

  const buildFormData = (base: Record<string, string>, topics: Topic[], studyFiles: File[], discussionFiles: File[]) => {
    const fd = new FormData();
    Object.entries(base).forEach(([k, v]) => fd.append(k, v));
    fd.append('topics', JSON.stringify(topics));
    studyFiles.forEach(f => fd.append('studyDocuments', f));
    discussionFiles.forEach(f => fd.append('discussionDocuments', f));
    return fd;
  };

  const validTopics = (topics: Topic[]) => topics.filter(t => t.text.trim());

  const handleSaveNew = async () => {
    setError('');
    const topics = validTopics(newMeeting.topics);
    if (!newMeeting.date || topics.length === 0) {
      setError('Date and at least one topic are required');
      return;
    }

    try {
      const fd = buildFormData({
        date: newMeeting.date,
        summary: newMeeting.summary,
        createdById: currentUser.id,
        createdByName: currentUser.name,
        createdByEmail: currentUser.email,
        createdByRole: currentUser.role
      }, topics, newStudyFiles, newDiscussionFiles);

      const response = await fetch('/api/tl-meetings', { method: 'POST', body: fd });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to save meeting');
      }

      const saved = await response.json();
      setMeetings([saved, ...meetings]);
      setExpandedDates(prev => new Set(prev).add(saved.date));
      setSuccess('Meeting saved successfully');
      setShowNewForm(false);
      setNewMeeting(emptyForm());
      setNewStudyFiles([]);
      setNewDiscussionFiles([]);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save meeting');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingData) return;
    setError('');
    const topics = validTopics(editingData.topics);
    if (topics.length === 0) {
      setError('At least one topic is required');
      return;
    }

    try {
      const fd = buildFormData({
        date: editingData.date,
        summary: editingData.summary,
        updatedById: currentUser.id,
        updatedByName: currentUser.name,
        updatedByEmail: currentUser.email,
        updatedByRole: currentUser.role
      }, topics, editStudyFiles, editDiscussionFiles);

      const response = await fetch(`/api/tl-meetings/${editingId}`, { method: 'PUT', body: fd });
      if (!response.ok) throw new Error('Failed to update meeting');

      const updated = await response.json();
      setMeetings(meetings.map(m => m._id === editingId ? updated : m));
      setExpandedDates(prev => new Set(prev).add(updated.date));
      setSuccess('Meeting updated successfully');
      setEditingId(null);
      setEditingData(null);
      setEditStudyFiles([]);
      setEditDiscussionFiles([]);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update meeting');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this meeting record?')) return;

    try {
      const response = await fetch(`/api/tl-meetings/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete meeting');

      setMeetings(meetings.filter(m => m._id !== id));
      setSuccess('Meeting deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete meeting');
    }
  };

  const handleRemoveDocument = async (meetingId: string, field: 'studyDocuments' | 'discussionDocuments', filename: string) => {
    if (!window.confirm('Remove this document?')) return;

    try {
      const response = await fetch(`/api/tl-meetings/${meetingId}/documents/${field}/${encodeURIComponent(filename)}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to remove document');

      const updated = await response.json();
      setMeetings(meetings.map(m => m._id === meetingId ? updated : m));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove document');
    }
  };

  // Documents are no longer public static files — they download through an
  // authenticated route, so a plain <a href> can't reach them (it sends no
  // token). Fetch with the session and hand the browser a blob to save.
  const downloadDocument = async (doc: MeetingDocument) => {
    try {
      const response = await fetch(doc.path);
      if (!response.ok) throw new Error('Could not download this document');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not download this document');
    }
  };

  const renderDocumentChips = (docs: MeetingDocument[], meetingId: string, field: 'studyDocuments' | 'discussionDocuments') => (
    <div className="flex flex-wrap gap-2">
      {docs.map(doc => (
        <div key={doc.filename} className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-full pl-3 pr-1.5 py-1">
          <button type="button" onClick={() => downloadDocument(doc)} className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700">
            <Paperclip className="w-3 h-3" />
            {doc.originalName}
            <span className="text-gray-400">{formatBytes(doc.size)}</span>
          </button>
          {canEdit && (
            <button
              onClick={() => handleRemoveDocument(meetingId, field, doc.filename)}
              className="p-0.5 text-gray-400 hover:text-red-600 rounded-full"
              title="Remove document"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  );

  // Group chronologically-sorted meetings under one heading per date, so a
  // long history reads as distinct days rather than one undifferentiated feed.
  const groupedByDate: { date: string; meetings: TLMeeting[] }[] = [];
  // A back-dated meeting is prepended and an edited date stays in place, so the
  // array isn't reliably sorted — order explicitly, newest day first.
  [...meetings].sort((a, b) => b.date.localeCompare(a.date)).forEach(meeting => {
    const group = groupedByDate.find(g => g.date === meeting.date);
    if (group) group.meetings.push(meeting);
    else groupedByDate.push({ date: meeting.date, meetings: [meeting] });
  });

  const toggleDateExpand = (date: string) => {
    const next = new Set(expandedDates);
    if (next.has(date)) next.delete(date);
    else next.add(date);
    setExpandedDates(next);
  };

  // The collapsed-row heading — topic titles from every meeting logged that
  // day, so the day is scannable without opening it.
  const headingFor = (dayMeetings: TLMeeting[]) => {
    const titles = dayMeetings.flatMap(m => (m.topics || []).map(t => t.text)).filter(Boolean);
    if (titles.length === 0) return 'No topics logged';
    return titles.join(', ');
  };

  const renderMeetingBody = (meeting: TLMeeting) => (
    <>
      <div className="space-y-2 mb-3">
        {meeting.topics?.map((topic, idx) => (
          <div key={idx} className="flex items-start gap-2 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-lg px-3 py-2">
            <MessageCircleQuestion className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">{topic.text}</p>
              {topic.assignedTo?.length > 0 ? (
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  Presented by {topic.assignedTo.map(a => a.name).join(' & ')} — everyone else: bring questions
                </p>
              ) : (
                <p className="text-xs text-gray-500">No presenter assigned yet</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {meeting.summary && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Overall Summary</p>
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{meeting.summary}</p>
        </div>
      )}

      {meeting.studyDocuments?.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase mb-1.5">
            <FileText className="w-3.5 h-3.5" />
            Studied Documents
          </div>
          {renderDocumentChips(meeting.studyDocuments, meeting._id, 'studyDocuments')}
        </div>
      )}

      {meeting.discussionDocuments?.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase mb-1.5">
            <Download className="w-3.5 h-3.5" />
            Discussion Documents
          </div>
          {renderDocumentChips(meeting.discussionDocuments, meeting._id, 'discussionDocuments')}
        </div>
      )}

      {meeting.createdBy?.name && (
        <p className="text-xs text-gray-400 mt-3">Logged by {meeting.createdBy.name}</p>
      )}
    </>
  );

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CalendarIcon className="w-7 h-7" />
            TL Meetings
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Mon/Wed/Fri sync log — topics, presenters, studied material, and a summary for anyone who missed it</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchMeetings}
            disabled={loading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {canEdit && (
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Log Meeting
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600" />
          <p className="text-green-700 dark:text-green-400">{success}</p>
        </div>
      )}

      {/* New Meeting Form */}
      {showNewForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-4">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-bold">Log New Meeting</h2>
            <button onClick={resetNewForm} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date *</label>
            <input
              type="date"
              value={newMeeting.date}
              onChange={(e) => setNewMeeting({ ...newMeeting, date: e.target.value })}
              className="w-full md:w-1/2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            {newMeeting.date && <p className="text-xs text-gray-500 mt-1">{weekdayOf(newMeeting.date)}</p>}
          </div>

          <TopicsEditor
            topics={newMeeting.topics}
            onChange={(topics) => setNewMeeting({ ...newMeeting, topics })}
            tlUsers={tlUsers}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Overall Summary</label>
            <textarea
              value={newMeeting.summary}
              onChange={(e) => setNewMeeting({ ...newMeeting, summary: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="A short recap so anyone who missed the meeting can understand what happened..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Studied Document(s)</label>
              <input
                type="file"
                multiple
                onChange={(e) => setNewStudyFiles(Array.from(e.target.files || []))}
                className="w-full text-sm text-gray-600 dark:text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {newStudyFiles.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{newStudyFiles.map(f => f.name).join(', ')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Discussion Document(s)</label>
              <input
                type="file"
                multiple
                onChange={(e) => setNewDiscussionFiles(Array.from(e.target.files || []))}
                className="w-full text-sm text-gray-600 dark:text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {newDiscussionFiles.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{newDiscussionFiles.map(f => f.name).join(', ')}</p>
              )}
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button
              onClick={resetNewForm}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveNew}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Meeting
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      )}

      {/* Meeting List — grouped by date, collapsed to a one-line heading until clicked */}
      {!loading && groupedByDate.length > 0 && (
        <div className="space-y-3">
          {groupedByDate.map(group => {
            const isExpanded = expandedDates.has(group.date);
            return (
            <div key={group.date} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              <button
                type="button"
                onClick={() => toggleDateExpand(group.date)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <ChevronDown className={`w-5 h-5 text-gray-500 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                <div className="px-3 py-1.5 bg-gray-900 dark:bg-gray-700 text-white rounded-lg text-sm font-semibold shrink-0">
                  {formatDateHeading(group.date)}
                </div>
                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium shrink-0">{weekdayOf(group.date)}</span>
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{headingFor(group.meetings)}</span>
              </button>

              {isExpanded && (
              <div className="space-y-3 px-4 pb-4">
                {group.meetings.map(meeting => (
                  <div key={meeting._id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border-l-4 border-blue-500">
                    {editingId === meeting._id ? (
                      <div className="space-y-3">
                        <input
                          type="date"
                          value={editingData?.date || ''}
                          onChange={(e) => setEditingData({ ...editingData!, date: e.target.value })}
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />

                        <TopicsEditor
                          topics={editingData?.topics || [emptyTopic()]}
                          onChange={(topics) => setEditingData({ ...editingData!, topics })}
                          tlUsers={tlUsers}
                        />

                        <textarea
                          value={editingData?.summary || ''}
                          onChange={(e) => setEditingData({ ...editingData!, summary: e.target.value })}
                          rows={3}
                          placeholder="Overall summary"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Add Study Document(s)</label>
                            <input
                              type="file"
                              multiple
                              onChange={(e) => setEditStudyFiles(Array.from(e.target.files || []))}
                              className="w-full text-xs"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Add Discussion Document(s)</label>
                            <input
                              type="file"
                              multiple
                              onChange={(e) => setEditDiscussionFiles(Array.from(e.target.files || []))}
                              className="w-full text-xs"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => { setEditingId(null); setEditingData(null); setEditStudyFiles([]); setEditDiscussionFiles([]); }}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-1.5"
                          >
                            <Save className="w-3.5 h-3.5" />
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {canEdit && (
                          <div className="flex justify-end gap-1.5 mb-2">
                            <button
                              onClick={() => { setEditingId(meeting._id); setEditingData({ date: meeting.date, topics: meeting.topics?.length ? meeting.topics : [emptyTopic()], summary: meeting.summary }); }}
                              className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(meeting._id)}
                              className="p-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded hover:bg-red-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        {renderMeetingBody(meeting)}
                      </>
                    )}
                  </div>
                ))}
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && groupedByDate.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">No meetings logged yet</p>
          {canEdit && (
            <button
              onClick={() => setShowNewForm(true)}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Log the first meeting
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TLMeetings;
